<template>
  <AppLayout>
    <div class="container compliance-detail-page">
     

      <!-- Header Card -->
      <div class="card compliance-header-card">
        <div class="header-main">
          <div class="header-info">
            <div class="header-title-row">
              <span class="icon vehicle-icon"></span>
              <span class="vehicle-name">{{ record?.vehicle?.plateNumber || '-' }}</span>
              <span :class="['badge', statusBadgeClass]">{{ statusText }}</span>
            </div>
            <div class="header-meta-grid">
              <div class="meta-item">
                <span class="label">Type</span>
                <span class="value">{{ record?.type?.name || '-' }}</span>
              </div>
              <div class="meta-item">
                <span class="label">Expiry</span>
                <span class="value expiry" :class="expiryClass">{{ formatDate(record?.expiryDate) }}</span>
              </div>
              <div class="meta-item">
                <span class="label">Status</span>
                <span class="value">{{ statusText }}</span>
              </div>
              <div class="meta-item">
                <span class="label">Model</span>
                <span class="value">{{ record?.vehicle?.make || '-' }} {{ record?.vehicle?.model || '' }}</span>
              </div>
            </div>
          </div>
          <div class="actions">
            <button class="btn btn-style-2" @click="editCompliance"><i class="fa fa-edit"></i> Edit</button>
            <button class="btn btn-style-2" @click="uploadDocument"><i class="fa fa-upload"></i> Upload Document</button>
            <button class="btn btn-style-2" @click="renewCompliance"><i class="fa fa-sync"></i> Renew</button>
          </div>
        </div>
      </div>

      <!-- Alert Banner -->
      <div v-if="urgencyBanner" class="alert">
        <span class="icon">⚠️</span> {{ urgencyBanner }}
      </div>

      <!-- Details Card -->
      <div class="card grid compliance-details-grid">
        <div class="meta-item">
          <span class="label">Vehicle</span>
          <span class="value">{{ record?.vehicle?.plateNumber || '-' }}</span>
        </div>
        <div class="meta-item">
          <span class="label">Type</span>
          <span class="value">{{ record?.type?.name || '-' }}</span>
        </div>
        <div class="meta-item">
          <span class="label">Expiry</span>
          <span class="value expiry" :class="expiryClass">{{ formatDate(record?.expiryDate) }}</span>
        </div>
        <div class="meta-item">
          <span class="label"><span class="icon">🛡</span> Status</span>
          <span class="value">{{ statusText }}</span>
        </div>
        <div class="meta-item">
          <span class="label">Model</span>
          <span class="value">{{ record?.vehicle?.make || '-' }} {{ record?.vehicle?.model || '' }}</span>
        </div>
      </div>

      <!-- Timeline Card 
      <div class="card timeline-card">
        <FleetTimeline title="Compliance Timeline" :items="timelineItems" />
      </div>-->

      <!-- Audit Export Card -->
      <div class="card export-card">
        <FleetAuditExport
          file-prefix="compliance-audit"
          report-title="Compliance Audit Report"
          :report-id="reportId"
          :reviewed-by="reviewedBy"
          :reviewed-date="reviewedDate"
          :summary-rows="summaryRows"
          :timeline-items="timelineItems"
        />
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue';
import FleetAuditExport from './FleetAuditExport.vue';
import fleetApi from '@/api/fleet';

export default {
  name: 'FleetComplianceDetail',
  components: { AppLayout,  FleetAuditExport },
  data() {
    return {
      loading: false,
      record: null,
    };
  },
  computed: {
    recordId() {
      return this.record?.id || this.record?.compliance_id || this.$route.params.id;
    },
    reportId() {
      const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      return `FLT-CMP-${String(this.recordId)}-${datePart}`;
    },
    reviewedBy() {
      try {
        const user = JSON.parse(localStorage.getItem('user_cache') || 'null');
        return user?.name || 'System Reviewer';
      } catch (_) {
        return 'System Reviewer';
      }
    },
    reviewedDate() {
      return new Date().toLocaleDateString();
    },
    summaryRows() {
      if (!this.record) return [];
      return [
        { label: 'Vehicle', value: this.record?.vehicle?.plateNumber || '-' },
        { label: 'Compliance Type', value: this.record?.type?.name || '-' },
        { label: 'Expiry Date', value: this.formatDate(this.record.expiryDate) },
        { label: 'Status', value: this.statusText },
      ];
    },
    timelineItems() {
      if (!this.record) return [];
      return [
        {
          label: 'Compliance record active',
          time: 'Current',
          note: `${this.record?.type?.name || 'Compliance'} for ${this.record?.vehicle?.plateNumber || 'vehicle'}`,
        },
        {
          label: 'Compliance expiry target',
          time: this.formatDate(this.record?.expiryDate),
          note: `Current status: ${this.statusText}`,
        },
      ];
    },
    statusText() {
      if (!this.record) return '-';
      const status = (this.record.status || '').toUpperCase();
      if (status.includes('EXPIRED')) return 'Expired';
      if (status.includes('EXPIRING')) return 'Expiring Soon';
      if (!this.record.expiryDate) return 'Active';
      const expiry = new Date(this.record.expiryDate).getTime();
      const diffDays = Math.ceil((expiry - Date.now()) / (1000 * 60 * 60 * 24));
      if (diffDays < 0) return 'Expired';
      if (diffDays <= 30) return 'Expiring Soon';
      return 'Active';
    },
    statusBadgeClass() {
      if (!this.record) return 'badge';
      const status = (this.record.status || '').toUpperCase();
      if (status.includes('EXPIRED')) return 'badge badge-danger';
      if (status.includes('EXPIRING')) return 'badge badge-warning';
      if (!this.record.expiryDate) return 'badge badge-success';
      const expiry = new Date(this.record.expiryDate).getTime();
      const diffDays = Math.ceil((expiry - Date.now()) / (1000 * 60 * 60 * 24));
      if (diffDays < 0) return 'badge badge-danger';
      if (diffDays <= 30) return 'badge badge-warning';
      return 'badge badge-success';
    },
    expiryClass() {
      if (!this.record) return '';
      const status = (this.record.status || '').toUpperCase();
      if (status.includes('EXPIRED')) return 'expired';
      if (status.includes('EXPIRING')) return 'warning';
      if (!this.record.expiryDate) return 'active';
      const expiry = new Date(this.record.expiryDate).getTime();
      const diffDays = Math.ceil((expiry - Date.now()) / (1000 * 60 * 60 * 24));
      if (diffDays < 0) return 'expired';
      if (diffDays <= 30) return 'warning';
      return 'active';
    },
    urgencyBanner() {
      if (!this.record) return '';
      const status = (this.record.status || '').toUpperCase();
      if (status.includes('EXPIRED')) return 'This compliance has expired! Immediate action required.';
      if (status.includes('EXPIRING')) {
        const expiry = new Date(this.record.expiryDate).getTime();
        const days = Math.ceil((expiry - Date.now()) / (1000 * 60 * 60 * 24));
        return `This compliance expires in ${days} day${days === 1 ? '' : 's'}`;
      }
      if (!this.record.expiryDate) return '';
      const expiry = new Date(this.record.expiryDate).getTime();
      const diffDays = Math.ceil((expiry - Date.now()) / (1000 * 60 * 60 * 24));
      if (diffDays < 0) return 'This compliance has expired! Immediate action required.';
      if (diffDays <= 30) return `This compliance expires in ${diffDays} day${diffDays === 1 ? '' : 's'}`;
      return '';
    },
  },
  methods: {
    formatDate(v) {
      if (!v) return 'N/A';
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString();
    },
    async loadDetail() {
      const id = this.$route.params.id;
      this.loading = true;
      try {
        const list = await fleetApi.listCompliances({ page: 1, limit: 200 });
        const rows = fleetApi.unwrapList(list);
        const fromList = rows.find((r) => String(r.id || r.compliance_id) === String(id));
        if (fromList) {
          this.record = fromList;
          return;
        }

        const API_BASE_URL = (process.env.VUE_APP_URL || 'http://localhost:3000').replace(/\/+$/, '');
        const token = JSON.parse(localStorage.getItem('access_token') || 'null');
        const res = await fetch(`${API_BASE_URL}/compliances/${id}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) throw new Error('Compliance fetch failed');
        this.record = await res.json();
      } catch (err) {
        this.$toast?.error?.(err?.message || 'Failed to load compliance record');
      } finally {
        this.loading = false;
      }
    },
    editCompliance() {
      // TODO: Implement edit action
      this.$toast?.info?.('Edit compliance (not implemented)');
    },
    uploadDocument() {
      // TODO: Implement upload document action
      this.$toast?.info?.('Upload document (not implemented)');
    },
    renewCompliance() {
      // TODO: Implement renew action
      this.$toast?.info?.('Renew compliance (not implemented)');
    },
  },
  mounted() {
    this.loadDetail();
  },
};
</script>

<style scoped>
@import './fleet.shared.css';

.compliance-detail-page {
  padding-top: 24px;
}

.breadcrumb {
  font-size: 13px;
  color: #6b7a90;
  margin-bottom: 12px;
}

.compliance-header-card {
  margin-bottom: 18px;
  display: flex;
  flex-direction: column;
  gap: 0;
}
.header-main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}
.header-info {
  flex: 1;
}
.header-title-row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
}
.vehicle-icon {
  font-size: 2rem;
}
.vehicle-name {
  font-size: 1.4rem;
  font-weight: 700;
}
.badge {
  margin-left: 12px;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 14px;
  border-radius: 20px;
  display: inline-block;
}
.header-meta-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}
.meta-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.label {
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 4px;
}
.value {
  font-size: 16px;
  font-weight: 500;
}
.expiry {
  font-size: 18px;
  font-weight: 600;
}
.expired { color: #dc2626; }
.warning { color: #f59e0b; }
.active { color: #16a34a; }

.actions {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}
.alert {
  background: #fff7ed;
  color: #c2410c;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.compliance-details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}
.timeline-card {
  margin-bottom: 20px;
}
.export-card {
  margin-bottom: 20px;
}
.icon {
  font-size: 1.1em;
  margin-right: 2px;
}
@media (max-width: 900px) {
  .header-meta-grid, .compliance-details-grid {
    grid-template-columns: 1fr;
  }
  .header-main {
    flex-direction: column;
    gap: 12px;
  }
}
</style>
